import React, { useState, useEffect, useRef, useCallback } from "react";
import { database } from "../firebaseConfig";
import { ref, get, onValue, update } from "firebase/database";
import "./LeadSpySettings.css";
import WebsiteAnalyzerSettings from "./WebsiteAnalyzerSettings";
import SeoAnalyzerSettings from "./SeoAnalyzerSettings";
import PotentialAnalyzerSettings from "./PotentialAnalyzerSettings";
import ListCleaningRulesSettings from "./ListCleaningRulesSettings";
import useResearchAccess from "../hooks/useResearchAccess";
import ResearchLockedNotice from "./ResearchLockedNotice";
import InstagramResearchSettings from "./InstagramResearchSettings";
import GoogleReviewAuditSettings from "./GoogleReviewAuditSettings";
import DataConnectorsSettings from "./DataConnectorsSettings";
import ChanceRadarSettings from "./ChanceRadarSettings";
import { isResearchModuleVisible } from "./instagramResearchLanguage";
import { PLAN_NAV_BADGE } from "../config/creditPlans";
import { potentialProfilesPath } from "./potentialProfiles";
import { DATA_CONNECTORS_PATH } from "./dataConnectors";

// Modules where the user creates named setups. Once at least one exists, the
// card shows that count instead of the plan pill — a quiet "already done".
const SETUP_SOURCES = {
  list_cleaning_rules: {
    path: (id) => `/World/${id}/settings/cleanupGuidelinesList`,
    one: 'Cleaner',
    many: 'Cleaners',
  },
  potential_analyzer: {
    path: potentialProfilesPath,
    one: 'Profile',
    many: 'Profiles',
  },
  data_connectors: {
    path: DATA_CONNECTORS_PATH,
    one: 'Researcher',
    many: 'Researchers',
  },
  chance_radar: {
    path: (id) => `/World/${id}/settings/chanceRadars`,
    one: 'Radar',
    many: 'Radars',
  },
};

const countRecords = (value) => {
  if (!value || typeof value !== 'object') return 0;
  return Object.values(value).filter((item) => item && typeof item === 'object').length;
};

const RESEARCH_GROUPS = [
  { id: 'clean', label: 'Clean' },
  { id: 'find', label: 'Research' },
];

const moduleIconClass = (icon) => `${icon.startsWith('fab') ? '' : 'fa '}${icon}`;

// Only the exceptions get a pill. "Unlocked" on every card would turn the
// store into a status dashboard; available is the quiet default, same as a
// live play in the Play Store.
const ModuleStatus = ({ mod, locked }) => {
  if (locked) return <span className="rsStatus"><i className="fa fa-lock" />Locked</span>;
  if (mod.isConfiguredElsewhere) return <span className="rsStatus">Via Company Data</span>;
  return null;
};

const moduleCover = (id) => `/img/research-studio/${id}.webp`;
const moduleGlyph = (id) => `/img/research-studio/icons/${id}.webp`;

const ModuleCard = ({ mod, locked, planBadge, setup, onOpen }) => {
  const [glyphFailed, setGlyphFailed] = useState(false);
  const countKnown = setup && typeof setup.count === 'number';
  const done = countKnown && setup.count > 0;
  const countLabel = done && setup.count === 1 ? setup.one : setup?.many;
  return (
  <button
    type="button"
    className={`rsCard ${locked ? 'rsCard--locked' : ''} ${mod.cover ? 'rsCard--cover' : ''}`}
    style={{ '--moduleColor': mod.color }}
    onClick={onOpen}
  >
    {mod.cover && (
      <div className="rsCover" aria-hidden="true">
        <img src={mod.cover} alt="" loading="lazy" />
      </div>
    )}
    {!locked && done ? (
      <span className="rsOwned is-set" title={`${setup.count} ${countLabel}`}>
        <i className="fa fa-check" aria-hidden="true" />
        <strong>{setup.count}</strong>
        {countLabel}
      </span>
    ) : !locked && planBadge && (!setup || countKnown) ? (
      <span className={`account-nav-badge ${planBadge.kind} rsPlan`}>{planBadge.label}</span>
    ) : null}
    <div className={`rsTile ${mod.glyph && !glyphFailed ? 'rsTile--glyph' : ''}`}>
      {mod.glyph && !glyphFailed ? (
        <img src={mod.glyph} alt="" loading="lazy" onError={() => setGlyphFailed(true)} />
      ) : (
        <i className={moduleIconClass(mod.icon)} />
      )}
    </div>
    <div className="rsCardInfo">
      <div className="rsMeta">
        <ModuleStatus mod={mod} locked={locked} />
        {mod.badge && <span className="rsKicker">{mod.badge}</span>}
      </div>
      <span className="rsName">{mod.name}</span>
      <span className="rsDesc">{mod.description}</span>
      <span className="altBtn altBtn--petrol rsCta">
        {locked ? 'Unlock' : 'Open'}
        <i className="fa fa-arrow-right" />
      </span>
    </div>
  </button>
  );
};

const ResearchCrumb = ({ current, onBack }) => (
  <nav className="rsCrumb" aria-label="Breadcrumb">
    <button type="button" className="rsCrumbBack" onClick={onBack}>
      <i className="fa fa-arrow-left" />
      Clean &amp; Enrich
    </button>
    <span className="rsCrumbSep">/</span>
    <span className="rsCrumbCurrent">{current}</span>
  </nav>
);

const ResearchSettingsPage = ({ userId }) => {
  const [openModuleId, setOpenModuleId] = useState(null);
  const [setupCounts, setSetupCounts] = useState({});
  const { research, researchLoading, researchContext, planId } = useResearchAccess();
  const planBadge = !researchLoading && planId ? PLAN_NAV_BADGE[planId] : null;

  useEffect(() => {
    if (!userId) return undefined;
    const unsubs = Object.entries(SETUP_SOURCES).map(([id, source]) =>
      onValue(ref(database, source.path(userId)), (snapshot) => {
        setSetupCounts((prev) => ({ ...prev, [id]: countRecords(snapshot.val()) }));
      }, () => setSetupCounts((prev) => ({ ...prev, [id]: 0 })))
    );
    return () => unsubs.forEach((unsub) => unsub());
  }, [userId]);

  // Ohne Lizenz gibt es nichts zu konfigurieren — sonst stellt jemand ein Tool
  // ein, das er anschliessend im Research-Dialog gesperrt vorfindet.
  const isLocked = (mod) =>
    !!mod.licenseKey && !researchLoading && research[mod.licenseKey] !== true;

  const allModules = [
    {
      id: 'list_cleaning_rules',
      group: 'clean',
      name: 'Clean Contact List',
      description: 'Guideline sets that keep or remove contacts, clean business names, and check emails.',
      icon: 'fa-broom',
      cover: moduleCover('list_cleaning_rules'),
      glyph: moduleGlyph('list_cleaning_rules'),
      color: 'var(--darkblue)',
      badge: 'List Cleaner',
      highlights: [
        { icon: 'fa-filter', label: 'Keep / remove rules' },
        { icon: 'fa-envelope-circle-check', label: 'Email checks' },
      ],
      component: <ListCleaningRulesSettings userId={userId} />,
    },
    {
      id: 'web_intelligence',
      group: 'find',
      licenseKey: 'hasWebResearch',
      name: 'Find Company Data',
      description: 'Configure the web crawler modules and what data to gather per contact',
      icon: 'fa-globe',
      cover: moduleCover('web_intelligence'),
      glyph: moduleGlyph('web_intelligence'),
      color: 'var(--blue)',
      badge: 'Web Intelligence',
      highlights: [
        { icon: 'fa-address-book', label: 'Emails, phones & DMs' },
        { icon: 'fa-building', label: 'Company profile' },
      ],
      component: <LeadSpySettings userId={userId} />,
    },
    {
      id: 'website_analyzer',
      group: 'find',
      licenseKey: 'hasWebsiteAnalyzer',
      name: 'Technical Analysis',
      description: 'Scan websites for CMS, shop data, product count & website age',
      icon: 'fa-laptop-code',
      cover: moduleCover('website_analyzer'),
      glyph: moduleGlyph('website_analyzer'),
      color: 'var(--accent-amber)',
      badge: 'Website Analyzer',
      highlights: [
        { icon: 'fa-microchip', label: 'CMS & shop' },
        { icon: 'fa-gauge-high', label: 'Size & age' },
      ],
      component: <WebsiteAnalyzerSettings userId={userId} />,
    },
    {
      id: 'seo_analyzer',
      group: 'find',
      licenseKey: 'hasSeoAnalyzer',
      name: 'Analyze SEO Visibility',
      description: 'Domain visibility, keyword rankings, competitors & backlinks, running on your own Sistrix API key',
      icon: 'fa-magnifying-glass-chart',
      cover: moduleCover('seo_analyzer'),
      glyph: moduleGlyph('seo_analyzer'),
      color: 'var(--accent-green)',
      badge: 'SEO Analyzer',
      highlights: [
        { icon: 'fa-chart-line', label: 'Visibility & traffic' },
        { icon: 'fa-arrow-trend-up', label: 'Ranking potential' },
      ],
      component: <SeoAnalyzerSettings userId={userId} />,
    },
    {
      id: 'linkedin_intelligence',
      group: 'find',
      licenseKey: 'hasWebResearch',
      name: 'Find Infos on LinkedIn',
      description: 'Dedicated LinkedIn profile discovery via Serper API that finds company pages & decision makers',
      icon: 'fab fa-linkedin',
      cover: moduleCover('linkedin_intelligence'),
      glyph: moduleGlyph('linkedin_intelligence'),
      // LinkedIn's own brand blue — the one place a platform colour beats ours
      color: '#0A66C2',
      badge: 'LinkedIn Intelligence',
      highlights: [
        { icon: 'fa-building', label: 'Company page' },
        { icon: 'fa-user-tie', label: 'Decision makers' },
      ],
      component: null,
      isConfiguredElsewhere: true,
      infoText: 'This module runs automatically when enabled in the Find Company Data settings. It uses the Serper API to perform targeted Google searches (site:linkedin.com) for company profiles and employee pages. Found LinkedIn profiles are merged into Decision Makers automatically.'
    },
    {
      id: 'instagram_research',
      group: 'find',
      licenseKey: 'hasInstagramResearch',
      hideIfUnlicensed: true,
      name: 'Instagram Finder',
      description: 'Pull profile insights from Instagram posts and choose whether they are stored in German or English',
      icon: 'fab fa-instagram',
      cover: moduleCover('instagram_research'),
      glyph: moduleGlyph('instagram_research'),
      color: '#E4405F',
      badge: 'Instagram Research',
      highlights: [
        { icon: 'fa-clapperboard', label: 'Posts & activity' },
        { icon: 'fa-language', label: 'DE / EN save language' },
      ],
      component: <InstagramResearchSettings userId={userId} />,
    },
    {
      id: 'google_review_audit',
      group: 'find',
      licenseKey: 'hasGoogleReviewAudit',
      name: 'Google Review Audit',
      description: 'Reads the Google reviews of every contact and pulls out complaint reasons, weak spots and what customers praise',
      icon: 'fa-star-half-stroke',
      cover: moduleCover('google_review_audit'),
      glyph: moduleGlyph('google_review_audit'),
      color: 'var(--accent-amber)',
      badge: 'Review Intelligence',
      highlights: [
        { icon: 'fa-triangle-exclamation', label: 'Top complaints' },
        { icon: 'fa-thumbs-up', label: 'Praised strengths' },
      ],
      component: <GoogleReviewAuditSettings userId={userId} />,
    },
    {
      id: 'data_connectors',
      group: 'find',
      licenseKey: 'hasDataConnectors',
      name: 'Custom Researchers',
      description: 'Connect any platform with an API and enrich contacts with its data, set up by an assistant that reads the API docs',
      icon: 'fa-plug',
      cover: moduleCover('data_connectors'),
      glyph: moduleGlyph('data_connectors'),
      color: 'var(--darkblue)',
      badge: 'Data Sources',
      highlights: [
        { icon: 'fa-wand-magic-sparkles', label: 'AI setup' },
        { icon: 'fa-lock', label: 'Encrypted keys' },
      ],
      component: <DataConnectorsSettings userId={userId} />,
    },
    {
      id: 'chance_radar',
      group: 'find',
      name: 'Chance Radar',
      description: 'Finds live chances in a contact’s market with a cheaper Google search. The text goes into the email context. A hit can be off.',
      icon: 'fa-satellite-dish',
      color: 'var(--darkblue)',
      badge: 'Live signals',
      highlights: [
        { icon: 'fa-magnifying-glass', label: 'Keywords, then Google' },
        { icon: 'fa-align-left', label: 'Text in the email context' },
      ],
      component: <ChanceRadarSettings userId={userId} />,
    },
    {
      id: 'potential_analyzer',
      group: 'clean',
      licenseKey: 'hasPotentialAnalyzer',
      name: 'Rate Lead from 1–10',
      description: 'AI qualifier that scores every contact from 1 to 10. Set up multiple ICP profiles and pick one per Outreach Agent.',
      icon: 'fa-bullseye',
      cover: moduleCover('potential_analyzer'),
      glyph: moduleGlyph('potential_analyzer'),
      color: 'var(--accent-indigo)',
      badge: 'Potential Analyzer',
      highlights: [
        { icon: 'fa-star-half-stroke', label: 'Score 1–10' },
        { icon: 'fa-layer-group', label: 'Multiple ICP profiles' },
      ],
      component: <PotentialAnalyzerSettings userId={userId} />,
    },
  ];

  // Der List Cleaner hat bewusst keinen licenseKey — er ist keine Recherche,
  // sondern Aufräumen, und bleibt für alle offen. Instagram Finder ist
  // hideIfUnlicensed: ohne Lizenz unsichtbar, nicht ausgegraut.
  const isVisible = (mod) => isResearchModuleVisible(mod, { research, researchLoading });

  const openModule = allModules.find((mod) => mod.id === openModuleId && isVisible(mod));

  if (openModule) {
    const locked = isLocked(openModule);
    const back = () => setOpenModuleId(null);
    return (
      <div className="rs">
        <ResearchCrumb current={openModule.name} onBack={back} />

        <div className="rsBody">
          {locked ? (
            <ResearchLockedNotice
              featureName={openModule.badge || openModule.name}
              accountEmail={researchContext.accountEmail}
              worldId={researchContext.worldId}
            />
          ) : openModule.component || (
            <div className="rsPlaceholder">
              <i className={moduleIconClass(openModule.icon)} />
              <span>{openModule.infoText || 'This module is configured per-contact during research. No global settings required yet.'}</span>
              {openModule.isConfiguredElsewhere && (
                <button
                  type="button"
                  className="altBtn altBtn--petrol"
                  onClick={() => setOpenModuleId('web_intelligence')}
                >
                  Open Find Company Data
                  <i className="fa fa-arrow-right" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rs">
      {RESEARCH_GROUPS.map((group) => {
        const modules = allModules.filter((mod) => mod.group === group.id && isVisible(mod));
        if (modules.length === 0) return null;
        return (
          <section className="rsSection" key={group.id}>
            <div className="rsSectionHead">
              <span className="rsSectionLabel">{group.label}</span>
              <span className="rsCount">{modules.length}</span>
            </div>
            <div className="rsGrid">
              {modules.map((mod) => (
                <ModuleCard
                  key={mod.id}
                  mod={mod}
                  locked={isLocked(mod)}
                  planBadge={planBadge}
                  setup={SETUP_SOURCES[mod.id] ? {
                    count: setupCounts[mod.id],
                    one: SETUP_SOURCES[mod.id].one,
                    many: SETUP_SOURCES[mod.id].many,
                  } : null}
                  onOpen={() => setOpenModuleId(mod.id)}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export { ResearchSettingsPage };

// Spy Data Modules mit Credit-Kosten und Ziel-Feldern (benutzerfreundliche Namen)
const SPY_MODULES = {
  // Core Data (immer aktiv)
  core: {
    id: 'core',
    name: 'Core Intelligence',
    description: 'Basic web search and business information gathering',
    icon: 'fa-search',
    credits: 1,
    isRequired: true,
    features: ['Google Search', 'Business Name Verification', 'Website Detection'],
    targetFields: ['Confidence Score']
  },
  
  // Email Discovery
  email: {
    id: 'email',
    name: 'Email Discovery',
    description: 'Find and validate business email addresses',
    icon: 'fa-envelope',
    credits: 1,
    isRequired: false,
    features: ['Email Extraction', 'Format Validation', 'Domain Verification', 'Spam Filter'],
    targetFields: ['Email']
  },
  
  // Phone Discovery
  phone: {
    id: 'phone',
    name: 'Phone Discovery',
    description: 'Extract and validate phone numbers',
    icon: 'fa-phone-alt',
    credits: 0.5,
    isRequired: false,
    features: ['Phone Extraction', 'International Format', 'Mobile Detection'],
    targetFields: ['Telephone']
  },
  
  // Social Media
  social: {
    id: 'social',
    name: 'Social Media Profiles',
    description: 'Discover social media presence across platforms',
    icon: 'fa-share-alt',
    credits: 1,
    isRequired: false,
    features: ['Instagram', 'Facebook', 'LinkedIn', 'TikTok', 'Twitter/X', 'YouTube'],
    targetFields: ['Instagram', 'Facebook', 'LinkedIn', 'TikTok', 'Twitter', 'YouTube']
  },
  
  // Enhanced Description
  description: {
    id: 'description',
    name: 'AI Business Summary',
    description: 'Generate intelligent business descriptions with current news',
    icon: 'fa-file-alt',
    credits: 1.5,
    isRequired: false,
    features: ['Company Overview', 'Services & Products', 'Key Highlights', 'Recent News'],
    targetFields: ['Description']
  },
  
  // Decision Makers / Entscheider
  decisionMakers: {
    id: 'decisionMakers',
    name: 'Decision Makers',
    description: 'Find key executives and decision makers within the company',
    icon: 'fa-user-tie',
    credits: 2,
    isRequired: false,
    isPremium: true,
    features: ['CEO & Founders', 'C-Level Executives', 'Department Heads', 'LinkedIn Profiles', 'Direct Contact Info'],
    targetFields: ['Decision Makers']
  },
  
  // LinkedIn Intelligence (Serper-powered)
  linkedinIntelligence: {
    id: 'linkedinIntelligence',
    name: 'LinkedIn Intelligence',
    description: 'Targeted LinkedIn profile discovery for company pages and decision makers',
    icon: 'fab fa-linkedin',
    credits: 1.5,
    isRequired: false,
    isPremium: true,
    features: ['Company Page Discovery', 'Employee Profile Search', 'C-Level Finder', 'Profile Scoring'],
    targetFields: ['LinkedIn', 'Decision Makers']
  },

  // Deep Website Crawl
  deepCrawl: {
    id: 'deepCrawl',
    name: 'Deep Website Crawl',
    description: 'Crawl multiple pages: Impressum, Contact, About, Team',
    icon: 'fa-spider',
    credits: 2,
    isRequired: false,
    isPremium: true,
    features: ['Multi-Page Crawl', 'Impressum Analysis', 'Contact Page Scan', 'Team Discovery'],
    targetFields: ['Address', 'Description']
  },

  // Shop Intelligence — focused parallel Grok call profiling e-commerce shops
  shopIntelligence: {
    id: 'shopIntelligence',
    name: 'Shop Intelligence',
    description: 'Detect online shops: platform, product count, categories, age, customer ratings',
    icon: 'fa-shopping-cart',
    credits: 1.5,
    isRequired: false,
    isPremium: true,
    features: ['Shop Detection', 'Platform (Shopify/WooCommerce/...)', 'Product Catalog Size', 'Trustpilot/Trusted Shops Score', 'Notable Brands'],
    targetFields: ['Description']
  },

  // Company Profile — focused parallel Grok call profiling company size & revenue
  companyProfile: {
    id: 'companyProfile',
    name: 'Company Size & Revenue',
    description: 'Estimate employees, revenue, growth signals, headquarters and ownership status',
    icon: 'fa-building',
    credits: 1.5,
    isRequired: false,
    isPremium: true,
    features: ['Employee Range', 'Revenue Estimate', 'Founded Year', 'HQ & Locations', 'Public/Private', 'Growth Signals'],
    targetFields: ['Description']
  },

  // SEO Audit — focused parallel Grok call auditing on-page SEO of the company website
  seoAudit: {
    id: 'seoAudit',
    name: 'SEO Audit',
    description: 'Visit the company website and audit on-page SEO: meta tags, headings, speed, mobile, technical issues',
    icon: 'fa-magnifying-glass-chart',
    credits: 2,
    isRequired: false,
    isPremium: true,
    features: ['Title & Meta Tags', 'Heading Structure', 'Mobile & Speed', 'HTTPS & Indexability', 'Critical Issues', 'Quick Wins'],
    targetFields: ['Description']
  }
};

const DEBOUNCE_MS = 1200;

const LeadSpySettings = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const [activeModules, setActiveModules] = useState(['core', 'email', 'phone', 'social', 'description']);
  const [userCredits, setUserCredits] = useState(0);
  const [spyStats, setSpyStats] = useState({ total: 0, thisMonth: 0 });
  
  const [advancedSettings, setAdvancedSettings] = useState({
    autoApplyHighConfidence: true,
    confidenceThreshold: 50,
    language: 'auto',
    targetCountry: 'auto',
    preserveExistingData: true,
    maxEmailsPerContact: 3,
    includeSocialProfiles: ['instagram', 'facebook', 'linkedin', 'tiktok']
  });
  
  const [customContext, setCustomContext] = useState('');

  const debounceRef = useRef(null);
  const savedTimerRef = useRef(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!userId) return;

    setLoading(true);
    const settingsRef = ref(database, `/World/${userId}/settings/spySettings`);
    get(settingsRef).then((snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        // Drop the retired 'leadPotential' module — it has been replaced by the
        // dedicated Potential Analyser. Filtering here cleans up legacy configs
        // (the next debounced save persists the pruned list).
        if (data.activeModules) setActiveModules(data.activeModules.filter(m => m !== 'leadPotential'));
        if (data.advancedSettings) setAdvancedSettings(prev => ({ ...prev, ...data.advancedSettings }));
        if (data.customContext) setCustomContext(data.customContext);
      }
      setLoading(false);
      requestAnimationFrame(() => { hasLoadedRef.current = true; });
    });

    const creditsRef = ref(database, `/World/${userId}/credits`);
    const unsubCredits = onValue(creditsRef, (snapshot) => {
      if (snapshot.exists()) {
        const wallets = snapshot.val() || {};
        setUserCredits((Number(wallets.spyCredits) || 0) + (Number(wallets.bonusCredits) || 0));
      }
    });

    const statsRef = ref(database, `/World/${userId}/stats/spy`);
    const unsubStats = onValue(statsRef, (snapshot) => {
      if (snapshot.exists()) {
        setSpyStats(snapshot.val());
      }
    });

    return () => {
      unsubCredits();
      unsubStats();
    };
  }, [userId]);

  const persistSettings = useCallback(async (modules, advanced, context) => {
    if (!userId) return;
    setSaveStatus('saving');
    try {
      const settingsRef = ref(database, `/World/${userId}/settings/spySettings`);
      await update(settingsRef, {
        activeModules: modules,
        advancedSettings: advanced,
        customContext: context,
        updatedAt: Date.now()
      });
      setSaveStatus('saved');
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2400);
    } catch (error) {
      console.error("Error saving spy settings:", error);
      setSaveStatus('idle');
    }
  }, [userId]);

  useEffect(() => {
    if (!hasLoadedRef.current) return;
    clearTimeout(debounceRef.current);
    setSaveStatus('idle');
    debounceRef.current = setTimeout(() => {
      persistSettings(activeModules, advancedSettings, customContext);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [activeModules, advancedSettings, customContext, persistSettings]);

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      clearTimeout(savedTimerRef.current);
    };
  }, []);

  const calculateCredits = (modules = activeModules) => {
    return modules.reduce((total, moduleId) => {
      const module = SPY_MODULES[moduleId];
      return total + (module?.credits || 0);
    }, 0);
  };

  const toggleModule = (moduleId) => {
    const module = SPY_MODULES[moduleId];
    if (module?.isRequired) return;
    setActiveModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const updateAdvancedSetting = (key, value) => {
    setAdvancedSettings(prev => ({ ...prev, [key]: value }));
  };

  const toggleSocialProfile = (platform) => {
    setAdvancedSettings(prev => ({
      ...prev,
      includeSocialProfiles: prev.includeSocialProfiles.includes(platform)
        ? prev.includeSocialProfiles.filter(p => p !== platform)
        : [...prev.includeSocialProfiles, platform]
    }));
  };

  if (loading) {
    return (
      <div className="spySettingsLoading">
        <i className="fa fa-spinner fa-spin"></i>
      </div>
    );
  }

  const totalCredits = calculateCredits();

  return (
    <div className="spySettingsContainer">
      {/* Header with Credit Display */}
      <div className="spySettingsHeader">
        <div className="spySettingsTitle">
          <div className="spySettingsIcon">
            <img src="/img/binoculars.svg" alt="Spy" />
          </div>
          <div className="spySettingsTitleText">
            <h3>Find Company Data</h3>
            <p>Customize what data the intelligence crawler should gather</p>
          </div>
        </div>
        <div className="spyCreditsDisplay">
          <div className="creditsBalance">
            <span className="creditsLabel">Available Credits</span>
            <span className="creditsValue">{userCredits.toLocaleString()}</span>
          </div>
          <div className="creditsCost">
            <span className="costLabel">Per Spy</span>
            <span className="costValue">{totalCredits} Credits</span>
          </div>
        </div>
      </div>

      {/* Modules Section */}
      <div className="spyModulesSection">
        <div className="modulesSectionHeader">
          <h4>
            <i className="fa fa-puzzle-piece"></i>
            Data Modules
          </h4>
          <div className="modulesSummary">
            <span className="modulesActive">{activeModules.length} active</span>
            <span className="modulesDivider">•</span>
            <span className="modulesCredits">{totalCredits} credits/spy</span>
          </div>
        </div>

        <div className="modulesGrid">
          {Object.entries(SPY_MODULES).map(([moduleId, module]) => (
            <div
              key={moduleId}
              className={`moduleCard ${activeModules.includes(moduleId) ? 'active' : ''} ${module.isRequired ? 'required' : ''} ${module.isPremium ? 'premium' : ''}`}
              onClick={() => toggleModule(moduleId)}
            >
              <div className="moduleCardHeader">
                <div className="moduleIcon">
                  <i className={`${module.icon.startsWith('fab') ? '' : 'fa '}${module.icon}`}></i>
                </div>
                <div className="moduleToggle">
                  {module.isRequired ? (
                    <span className="requiredBadge">Required</span>
                  ) : (
                    <div className={`toggleSwitch ${activeModules.includes(moduleId) ? 'on' : ''}`}>
                      <div className="toggleKnob"></div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="moduleCardBody">
                <h5 className="moduleName">
                  {module.name}
                  {module.isPremium && <i className="fa fa-crown premiumIcon"></i>}
                </h5>
                <p className="moduleDesc">{module.description}</p>
              </div>
              
              <div className="moduleCardFooter">
                <div className="moduleFeatures">
                  {module.features.slice(0, 3).map((feature, idx) => (
                    <span key={idx} className="moduleFeature">
                      <i className="fa fa-check"></i>
                      {feature}
                    </span>
                  ))}
                  {module.features.length > 3 && (
                    <span className="moduleFeatureMore">+{module.features.length - 3} more</span>
                  )}
                </div>
                <div className="moduleCredits">
                  <span className="creditValue">{module.credits}</span>
                  <span className="creditLabel">credits</span>
                </div>
              </div>
              
              {/* Target Fields Info */}
              <div className="moduleTargetFields">
                <i className="fa fa-database"></i>
                <span className="targetFieldsLabel">Saves to:</span>
                <span className="targetFieldsList">
                  {module.targetFields.map((field, idx) => (
                    <code key={idx} className="targetField">{field}</code>
                  ))}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Credit Calculator */}
      <div className="creditCalculatorSection">
        <div className="calculatorCard">
          <div className="calculatorHeader">
            <h4>
              <i className="fa fa-calculator"></i>
              Credit Calculator
            </h4>
          </div>
          <div className="calculatorBody">
            <div className="calculatorRow">
              <span className="calcLabel">Credits per Spy</span>
              <span className="calcValue">{totalCredits}</span>
            </div>
            <div className="calculatorRow">
              <span className="calcLabel">Your Balance</span>
              <span className="calcValue">{userCredits.toLocaleString()}</span>
            </div>
            <div className="calculatorDivider"></div>
            <div className="calculatorRow highlight">
              <span className="calcLabel">Possible Spys</span>
              <span className="calcValue">{totalCredits > 0 ? Math.floor(userCredits / totalCredits).toLocaleString() : '∞'}</span>
            </div>
          </div>
          <div className="calculatorFooter">
            <button className="buyCreditsBtn">
              <i className="fa fa-plus-circle"></i>
              Get More Credits
            </button>
          </div>
        </div>

        <div className="calculatorCard statsCard">
          <div className="calculatorHeader">
            <h4>
              <i className="fa fa-chart-bar"></i>
              Usage Statistics
            </h4>
          </div>
          <div className="calculatorBody">
            <div className="calculatorRow">
              <span className="calcLabel">Total Spys</span>
              <span className="calcValue">{spyStats.total?.toLocaleString() || 0}</span>
            </div>
            <div className="calculatorRow">
              <span className="calcLabel">This Month</span>
              <span className="calcValue">{spyStats.thisMonth?.toLocaleString() || 0}</span>
            </div>
            <div className="calculatorRow">
              <span className="calcLabel">Credits Used</span>
              <span className="calcValue">{(spyStats.creditsUsed || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Context Section */}
      <div className={`customContextSection ${customContext.length > 0 ? 'has-content' : ''}`}>
        <div className="customContextHeader">
          <div className="contextTitleGroup">
            <h4>
              <i className="fa fa-wand-magic-sparkles"></i>
              AI Instructions
            </h4>
            {customContext.length > 0 && (
              <span className="contextActiveIndicator">
                <span className="activeDot"></span>
                Active
              </span>
            )}
          </div>
          <div className="contextHeaderActions">
            {customContext.length > 0 && (
              <button 
                className="contextClearBtn"
                onClick={() => setCustomContext('')}
                title="Clear instructions"
              >
                <i className="fa fa-times"></i>
              </button>
            )}
          </div>
        </div>
        
        <div className="customContextBody">
          <div className="textareaWrapper">
            <div className="textareaGutter">
              <span className="gutterIcon"><i className="fa fa-terminal"></i></span>
            </div>
            <textarea
              className="customContextInput"
              placeholder="Tell the AI what matters most to you...

Example: Focus on e-commerce companies. Find marketing decision makers. Prioritize contacts with direct phone numbers."
              value={customContext}
              onChange={(e) => setCustomContext(e.target.value)}
              maxLength={500}
            />
          </div>
          
          <div className="contextFooter">
            <div className="charProgress">
              <div 
                className="charProgressBar" 
                style={{ width: `${(customContext.length / 500) * 100}%` }}
              ></div>
            </div>
            <span className="charCount">{customContext.length}<span className="charMax">/500</span></span>
          </div>

          <div className="templatesSection">
            <span className="templatesLabel">Quick Add</span>
            <div className="templatesPills">
              <button 
                className="templatePill"
                onClick={() => setCustomContext(prev => prev + (prev ? ' ' : '') + 'Focus on e-commerce companies.')}
              >
                E-Commerce
              </button>
              <button 
                className="templatePill"
                onClick={() => setCustomContext(prev => prev + (prev ? ' ' : '') + 'Find marketing decision makers.')}
              >
                Marketing
              </button>
              <button 
                className="templatePill"
                onClick={() => setCustomContext(prev => prev + (prev ? ' ' : '') + 'Prioritize companies with 50+ employees.')}
              >
                Enterprise
              </button>
              <button 
                className="templatePill"
                onClick={() => setCustomContext(prev => prev + (prev ? ' ' : '') + 'Find direct phone numbers.')}
              >
                Direct Lines
              </button>
              <button 
                className="templatePill"
                onClick={() => setCustomContext(prev => prev + (prev ? ' ' : '') + 'Focus on SaaS and tech companies.')}
              >
                SaaS / Tech
              </button>
              <button 
                className="templatePill"
                onClick={() => setCustomContext(prev => prev + (prev ? ' ' : '') + 'Find CEO and founder contacts.')}
              >
                C-Level
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Settings */}
      <div className="advancedSettingsSection">
        <div className="advancedHeader">
          <h4>
            <i className="fa fa-cog"></i>
            Advanced Settings
          </h4>
        </div>
        
        <div className="advancedGrid">
          {/* Auto Apply */}
          <div className="advancedCard">
            <div className="advancedCardHeader">
              <div className="advancedCardIcon">
                <i className="fa fa-bolt"></i>
              </div>
              <div className="advancedCardTitle">
                <h5>Auto-Apply Results</h5>
                <p>Automatically apply spy data when confidence is high</p>
              </div>
              <div 
                className={`toggleSwitch ${advancedSettings.autoApplyHighConfidence ? 'on' : ''}`}
                onClick={() => updateAdvancedSetting('autoApplyHighConfidence', !advancedSettings.autoApplyHighConfidence)}
              >
                <div className="toggleKnob"></div>
              </div>
            </div>
            {advancedSettings.autoApplyHighConfidence && (
              <div className="advancedCardBody">
                <label>Confidence Threshold</label>
                <div className="thresholdSlider">
                  <input
                    type="range"
                    min="30"
                    max="90"
                    value={advancedSettings.confidenceThreshold}
                    onChange={(e) => updateAdvancedSetting('confidenceThreshold', parseInt(e.target.value))}
                  />
                  <span className="thresholdValue">{advancedSettings.confidenceThreshold}%</span>
                </div>
              </div>
            )}
          </div>

          {/* Target Market / Country */}
          <div className="advancedCard fullWidth">
            <div className="advancedCardHeader">
              <div className="advancedCardIcon">
                <i className="fa fa-flag"></i>
              </div>
              <div className="advancedCardTitle">
                <h5>Target Market / Country</h5>
                <p>Adapts search terms, Google region and email patterns to the country your leads are in</p>
              </div>
            </div>
            <div className="advancedCardBody">
              <div className="languageOptions">
                {[
                  { id: 'auto', label: '🌍 Auto-Detect' },
                  { id: 'de', label: '🇩🇪 DACH (DE/AT/CH)' },
                  { id: 'pl', label: '🇵🇱 Poland' },
                  { id: 'fr', label: '🇫🇷 France' },
                  { id: 'it', label: '🇮🇹 Italy' },
                  { id: 'es', label: '🇪🇸 Spain' },
                  { id: 'nl', label: '🇳🇱 Netherlands' },
                  { id: 'en', label: '🌐 International (English)' }
                ].map(opt => (
                  <button
                    key={opt.id}
                    className={`langOption ${advancedSettings.targetCountry === opt.id ? 'active' : ''}`}
                    onClick={() => updateAdvancedSetting('targetCountry', opt.id)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Language */}
          <div className="advancedCard">
            <div className="advancedCardHeader">
              <div className="advancedCardIcon">
                <i className="fa fa-globe"></i>
              </div>
              <div className="advancedCardTitle">
                <h5>Language Detection</h5>
                <p>Language for AI-generated descriptions</p>
              </div>
            </div>
            <div className="advancedCardBody">
              <div className="languageOptions">
                {['auto', 'English', 'German'].map(lang => (
                  <button
                    key={lang}
                    className={`langOption ${advancedSettings.language === lang ? 'active' : ''}`}
                    onClick={() => updateAdvancedSetting('language', lang)}
                  >
                    {lang === 'auto' ? '🌍 Auto-Detect' : lang === 'English' ? '🇬🇧 English' : '🇩🇪 German'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Preserve Existing */}
          <div className="advancedCard">
            <div className="advancedCardHeader">
              <div className="advancedCardIcon">
                <i className="fa fa-shield-alt"></i>
              </div>
              <div className="advancedCardTitle">
                <h5>Preserve Existing Data</h5>
                <p>Don't overwrite existing emails and phone numbers</p>
              </div>
              <div 
                className={`toggleSwitch ${advancedSettings.preserveExistingData ? 'on' : ''}`}
                onClick={() => updateAdvancedSetting('preserveExistingData', !advancedSettings.preserveExistingData)}
              >
                <div className="toggleKnob"></div>
              </div>
            </div>
          </div>

          {/* Social Profiles Selection */}
          <div className="advancedCard fullWidth">
            <div className="advancedCardHeader">
              <div className="advancedCardIcon">
                <i className="fa fa-share-alt"></i>
              </div>
              <div className="advancedCardTitle">
                <h5>Social Media Platforms</h5>
                <p>Select which platforms to search for</p>
              </div>
            </div>
            <div className="advancedCardBody">
              <div className="socialPlatformsGrid">
                {[
                  { id: 'instagram', icon: 'fa-instagram', name: 'Instagram', brand: true },
                  { id: 'facebook', icon: 'fa-facebook', name: 'Facebook', brand: true },
                  { id: 'linkedin', icon: 'fa-linkedin', name: 'LinkedIn', brand: true },
                  { id: 'tiktok', icon: 'fa-tiktok', name: 'TikTok', brand: true },
                  { id: 'twitter', icon: 'fa-twitter', name: 'Twitter/X', brand: true },
                  { id: 'youtube', icon: 'fa-youtube', name: 'YouTube', brand: true }
                ].map(platform => (
                  <button
                    key={platform.id}
                    className={`socialPlatformBtn ${advancedSettings.includeSocialProfiles.includes(platform.id) ? 'active' : ''}`}
                    onClick={() => toggleSocialProfile(platform.id)}
                  >
                    <i className={`${platform.brand ? 'fab' : 'fa'} ${platform.icon}`}></i>
                    <span>{platform.name}</span>
                    {advancedSettings.includeSocialProfiles.includes(platform.id) && (
                      <i className="fa fa-check checkIcon"></i>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`spyAutoSaveIndicator ${saveStatus}`}>
        {saveStatus === 'saving' && (
          <>
            <span className="autoSaveSpinner"></span>
            <span>Saving…</span>
          </>
        )}
        {saveStatus === 'saved' && (
          <>
            <i className="fa fa-check"></i>
            <span>Saved</span>
          </>
        )}
      </div>
    </div>
  );
};

export default LeadSpySettings;
